import type { ReactNode } from 'react'
import { Betrag } from './Betrag'
import { anzahlwort, kontobezeichnung } from './sprache'
import type { Regelparameter } from './typen'

/** Was ein Wert des Konzernhandbuchs ist — danach wird er geschrieben und erfasst. */
export type Handbuchwertart = 'betrag' | 'anzahl' | 'konto' | 'liste'

/**
 * **Jeder Wert des Konzernhandbuchs hat seine Art** (7.2 Abschnitt 53, R1633).
 *
 * Bis zum 2026-10-12 stand die Grenze für IC-Differenzen im Prozessfenster als
 * *10000*, im Konzernhandbuch als *10.000,00*. Die Liste ist vollständig über
 * alle Angaben des Konzernhandbuchs: Kommt eine hinzu, meldet die
 * Typprüfung, dass ihre Art fehlt.
 */
const ART: Record<keyof Regelparameter, Handbuchwertart> = {
  nachkommastellen: 'anzahl',
  icDifferenzGrenze: 'betrag',
  icDifferenzKonto: 'konto',
  icDifferenzKontoWaehrung: 'konto',
  firmenwertKonto: 'konto',
  unterschiedsbetragPassivKonto: 'konto',
  abschreibungFinanzanlagenKonto: 'konto',
  abgangBeteiligungenKonto: 'konto',
  nichtBeherrschendKonto: 'konto',
  zwischenergebnisKonto: 'konto',
  goodwillNutzungsdauerMonate: 'anzahl',
  firmenwertAbschreibungKonto: 'konto',
  gewinnvortragKonto: 'konto',
  icGrenzeJePaar: 'liste',
}

export function handbuchwertart(name: string): Handbuchwertart | undefined {
  return (ART as Record<string, Handbuchwertart>)[name]
}

/**
 * Ein Wert, wie er im Konzernhandbuch steht — ein Betrag als Betrag, eine
 * Anzahl als Anzahl, ein Konto mit Nummer und Namen (R1633).
 */
export function Handbuchwert({ name, wert }: { name: string; wert: unknown }): ReactNode {
  if (wert === null || wert === undefined || wert === '') {
    return null
  }

  const art = handbuchwertart(name)
  const zahl = typeof wert === 'number' ? wert : Number(wert)

  if (art === 'betrag' && Number.isFinite(zahl)) {
    return <Betrag wert={zahl} />
  }

  if (art === 'anzahl' && Number.isFinite(zahl)) {
    return anzahlwort(zahl)
  }

  if (art === 'konto' && typeof wert === 'string') {
    return kontobezeichnung(wert)
  }

  return String(wert)
}
